import { createRouter, createWebHistory } from 'vue-router'

// Route components (lazy loaded for better performance)
const LoginView = () => import('@/views/LoginView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const POSView = () => import('@/views/POSView.vue')
const ProductsView = () => import('@/views/ProductsView.vue')
const TransactionsView = () => import('@/views/TransactionsView.vue')
const CustomersView = () => import('@/views/CustomersView.vue')
const ReportsView = () => import('@/views/ReportsView.vue')
const SettingsView = () => import('@/views/SettingsView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
const StockManagementView = () => import('@/views/StockManagementView.vue')
const LabelPrintingView = () => import('@/views/LabelPrintingView.vue')
const TemplateManagementView = () => import('@/views/TemplateManagementView.vue')
const TemplateEditorView = () => import('@/views/TemplateEditorView.vue')
const SimpleStickerPrintView = () => import('@/views/SimpleStickerPrint.vue')
const TemplateTestingView = () => import('@/views/TemplateTesting.vue')
const ImagePrintingTestView = () => import('@/views/ImagePrintingTest.vue')

const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/sticker-print-simple',
    name: 'SimpleStickerPrint',
    component: SimpleStickerPrintView,
    meta: {
      requiresAuth: true,
      title: 'Simple Sticker Print - QuickBill POS'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { 
      requiresAuth: false,
      title: 'Login - QuickBill POS'
    }
  },
  {
    path: '/subscription',
    name: 'Subscription',
    component: () => import('@/views/SubscriptionView.vue'),
    meta: { 
      requiresAuth: true,
      allowExpiredSubscription: true, // Special flag to allow access even with expired subscription
      title: 'Subscription Management - QuickBill POS'
    }
  },
  {
    path: '/suspended',
    name: 'Suspended',
    component: () => import('@/views/SuspendedView.vue'),
    meta: { 
      requiresAuth: true,
      allowSuspendedSubscription: true, // Special flag to allow access even with suspended subscription
      title: 'Account Suspended - QuickBill POS'
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: { 
      requiresAuth: true,
      title: 'Dashboard - QuickBill POS'
    }
  },
  {
    path: '/pos',
    name: 'POS',
    component: POSView,
    meta: { 
      requiresAuth: true,
      title: 'Point of Sale - QuickBill POS',
      fullscreen: true // Hide navigation for distraction-free POS
    }
  },
  {
    path: '/products',
    name: 'Products',
    component: ProductsView,
    meta: { 
      requiresAuth: true,
      title: 'Products - QuickBill POS'
    }
  },
  {
    path: '/transactions',
    name: 'Transactions',
    component: TransactionsView,
    meta: { 
      requiresAuth: true,
      title: 'Transactions - QuickBill POS'
    }
  },
  {
    path: '/customers',
    name: 'Customers',
    component: CustomersView,
    meta: { 
      requiresAuth: true,
      title: 'Customers - QuickBill POS'
    }
  },
  {
    path: '/reports',
    name: 'Reports',
    component: ReportsView,
    meta: { 
      requiresAuth: true,
      title: 'Reports - QuickBill POS'
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsView,
    meta: { 
      requiresAuth: true,
      title: 'Settings - QuickBill POS'
    }
  },
  {
    path: '/stock-management',
    name: 'StockManagement',
    component: StockManagementView,
    meta: { 
      requiresAuth: true,
      title: 'Stock Management - QuickBill POS'
    }
  },
  {
    path: '/label-printing',
    name: 'LabelPrinting',
    component: LabelPrintingView,
    meta: { 
      requiresAuth: true,
      title: 'Label Printing - QuickBill POS'
    }
  },
  {
    path: '/template-management',
    name: 'TemplateManagement',
    component: TemplateManagementView,
    meta: { 
      requiresAuth: true,
      title: 'Template Management - QuickBill POS'
    }
  },
  {
    path: '/template-editor/:id?',
    name: 'TemplateEditor',
    component: TemplateEditorView,
    meta: { 
      requiresAuth: true,
      title: 'Template Editor - QuickBill POS'
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfileView,
    meta: { 
      requiresAuth: true,
      title: 'Profile - QuickBill POS'
    }
  },
  {
    path: '/template-testing',
    name: 'TemplateTesting',
    component: TemplateTestingView,
    meta: { 
      requiresAuth: true,
      title: 'Receipt Template Testing - QuickBill POS'
    }
  },
  {
    path: '/image-printing-test',
    name: 'ImagePrintingTest',
    component: ImagePrintingTestView,
    meta: { 
      requiresAuth: true,
      title: 'Image Printing Test - QuickBill POS'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Page Not Found - QuickBill POS'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top for mobile navigation
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Import stores inside the guard to ensure Pinia is ready
  const { useAuthStore } = await import('@/stores/auth')
  const { useSubscriptionStore } = await import('@/stores/subscription')
  
  const authStore = useAuthStore()
  const subscriptionStore = useSubscriptionStore()
  
  // Initialize auth store first
  authStore.initializeAuth()
  
  // Set page title
  document.title = to.meta.title || 'QuickBill POS'
  
  // Check authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if not authenticated
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }
  
  // Redirect to dashboard if already authenticated and trying to access login
  if (to.name === 'Login' && authStore.isAuthenticated) {
    next({ name: 'Dashboard' })
    return
  }
  
  // Check business context for authenticated routes
  if (to.meta.requiresAuth && authStore.isAuthenticated && !authStore.business) {
    // If no business context, try to load it
    try {
      await authStore.loadBusinessContext()
    } catch (error) {
      console.error('Failed to load business context:', error)
      // Redirect to login if business context fails
      authStore.logout()
      next({ name: 'Login' })
      return
    }
  }
  
  // Check subscription for authenticated routes (excluding superadmin)
  if (to.meta.requiresAuth && authStore.isAuthenticated && authStore.userRole !== 'superadmin') {

    
    // Check if subscription is suspended and not trying to access suspended page
    if (!to.meta.allowSuspendedSubscription && subscriptionStore.isSubscriptionSuspended) {

      next({ name: 'Suspended' })
      return
    }
    
    // Check if subscription is blocked and not trying to access subscription page
    if (!to.meta.allowExpiredSubscription && !to.meta.allowSuspendedSubscription && subscriptionStore.isAppBlocked) {
      if (subscriptionStore.isSubscriptionSuspended) {

        next({ name: 'Suspended' })
      } else {

        next({ name: 'Subscription' })
      }
      return
    }
  }
  
  next()
})

// After navigation
router.afterEach((to, from) => {
  // Add route-specific body classes for styling
  document.body.className = `route-${to.name?.toLowerCase() || 'unknown'}`
  
  // Add fullscreen class for POS view
  if (to.meta.fullscreen) {
    document.body.classList.add('fullscreen-mode')
  } else {
    document.body.classList.remove('fullscreen-mode')
  }
  
  // Log navigation for debugging

})

export default router
